import { type Clock, systemClock } from './clock.js';
import { type LocalStore, MemoryLocalStore } from './local-store.js';
import { LocalFirstMutationQueue, type MutationQueue } from './mutation.js';
import { GraphService } from './graph-service.js';
import { GrowthService } from './growth-service.js';
import { NotificationService } from './notification-service.js';
import { AiService, LocalAssistantProvider } from './ai-service.js';
import {
  MemoryCaptureRepository,
  MemoryReminderRepository,
  MemoryReviewRepository,
  MemoryRoutineRepository,
  MemoryProblemRepository,
  MemoryProjectRepository,
  MemoryEventRepository,
  MemoryFinanceRepository,
  MemoryNoteRepository,
  MemoryDirectionRepository,
  MemoryHealthRepository,
  MemoryStudyRepository,
  MemoryCareerRepository,
  MemoryJournalRepository,
  MemoryContentRepository,
  MemoryBusinessRepository,
  MemoryEnvironmentRepository,
  MemoryFocusRepository,
  MemorySecurityRepository,
  MemorySourceRepository,
  MemoryTargetRepository,
  MemoryTaskRepository,
  MemoryAcademicsRepository,
} from './memory-repositories.js';
import type {
  CaptureRepository,
  ReminderRepository,
  ReviewRepository,
  RoutineRepository,
  ProblemRepository,
  ProjectRepository,
  EventRepository,
  FinanceRepository,
  NoteRepository,
  DirectionRepository,
  HealthRepository,
  StudyRepository,
  CareerRepository,
  JournalRepository,
  ContentRepository,
  BusinessRepository,
  EnvironmentRepository,
  FocusRepository,
  SecurityRepository,
  SourceRepository,
  TargetRepository,
  TaskRepository,
  AcademicsRepository,
} from './repositories.js';
import { DataFeatureServices, type OwnerContext } from './services.js';
import type { FeatureServices } from '@life-os/app-core';

export interface Repositories {
  readonly source: SourceRepository;
  readonly capture: CaptureRepository;
  readonly tasks: TaskRepository;
  readonly reviews: ReviewRepository;
  readonly reminders: ReminderRepository;
  readonly targets: TargetRepository;
  readonly routines: RoutineRepository;
  readonly problems: ProblemRepository;
  readonly projects: ProjectRepository;
  readonly events: EventRepository;
  readonly finance: FinanceRepository;
  readonly notes: NoteRepository;
  readonly direction: DirectionRepository;
  readonly health: HealthRepository;
  readonly study: StudyRepository;
  readonly career: CareerRepository;
  readonly journal: JournalRepository;
  readonly content: ContentRepository;
  readonly business: BusinessRepository;
  readonly environment: EnvironmentRepository;
  readonly focus: FocusRepository;
  readonly security: SecurityRepository;
  readonly academics: AcademicsRepository;
}

export interface DataContext {
  readonly services: FeatureServices;
  readonly repositories: Repositories;
  readonly queue: MutationQueue;
  readonly graph: GraphService;
  readonly growth: GrowthService;
  readonly notifications: NotificationService;
  readonly ai: AiService;
  readonly store: LocalStore;
}

export interface CreateDataOptions {
  readonly ownerId?: string;
  readonly clock?: Clock;
  readonly store?: LocalStore;
}

/**
 * Wire the local-first data context over a store (in-memory by default).
 * The SQLite/OPFS adapter will plug in here by passing a different `store`.
 */
export function createDataContext(options: CreateDataOptions = {}): DataContext {
  const clock = options.clock ?? systemClock;
  const store = options.store ?? new MemoryLocalStore();
  const owner: OwnerContext = { ownerId: options.ownerId ?? 'owner' };

  const queue = new LocalFirstMutationQueue(store, clock);
  const graph = new GraphService(store, clock);
  const notifications = new NotificationService(store, clock);
  const ai = new AiService(new LocalAssistantProvider());
  const repositories: Repositories = {
    source: new MemorySourceRepository(store, clock),
    capture: new MemoryCaptureRepository(store, clock),
    tasks: new MemoryTaskRepository(store, clock),
    reviews: new MemoryReviewRepository(store, clock),
    reminders: new MemoryReminderRepository(store, clock),
    targets: new MemoryTargetRepository(store, clock),
    routines: new MemoryRoutineRepository(store, clock),
    problems: new MemoryProblemRepository(store, clock),
    projects: new MemoryProjectRepository(store, clock),
    events: new MemoryEventRepository(store, clock),
    finance: new MemoryFinanceRepository(store, clock),
    notes: new MemoryNoteRepository(store, clock),
    direction: new MemoryDirectionRepository(store, clock),
    health: new MemoryHealthRepository(store, clock),
    study: new MemoryStudyRepository(store, clock),
    career: new MemoryCareerRepository(store, clock),
    journal: new MemoryJournalRepository(store, clock),
    content: new MemoryContentRepository(store, clock),
    business: new MemoryBusinessRepository(store, clock),
    environment: new MemoryEnvironmentRepository(store, clock),
    focus: new MemoryFocusRepository(store, clock),
    security: new MemorySecurityRepository(store, clock),
    academics: new MemoryAcademicsRepository(store, clock),
  };

  const services = new DataFeatureServices({ owner, queue, clock, ...repositories });
  const growth = new GrowthService(services, graph);

  return { services, repositories, queue, graph, growth, notifications, ai, store };
}
